export class InputManager {
  private readonly pressedKeys = new Set<string>();
  private readonly justPressedKeys = new Set<string>();
  private readonly pressedButtons = new Set<number>();
  private readonly keyDownCallbacks = new Set<(code: string) => void>();
  private pointerX = 0;
  private pointerY = 0;
  private pointerDeltaX = 0;
  private pointerDeltaY = 0;
  private pointerLocked = false;
  private pointerLockTarget: HTMLElement | null = null;

  constructor() {
    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('keyup', this.handleKeyUp);
    window.addEventListener('mousedown', this.handleMouseDown);
    window.addEventListener('mouseup', this.handleMouseUp);
    window.addEventListener('mousemove', this.handleMouseMove);
    window.addEventListener('blur', this.handleBlur);
    document.addEventListener('pointerlockchange', this.handlePointerLockChange);
  }

  setPointerLockTarget(element: HTMLElement): void {
    this.pointerLockTarget = element;
  }

  requestPointerLock(): void {
    if (this.pointerLocked) return;
    const target = this.pointerLockTarget;
    if (target && typeof target.requestPointerLock === 'function') {
      try {
        target.requestPointerLock();
      } catch {
        /* ignore */
      }
    }
  }

  exitPointerLock(): void {
    if (this.pointerLocked) document.exitPointerLock();
  }

  isPointerLocked(): boolean {
    return this.pointerLocked;
  }

  isKeyDown(code: string): boolean {
    return this.pressedKeys.has(code);
  }

  isKeyJustPressed(code: string): boolean {
    return this.justPressedKeys.has(code);
  }

  /** Removes a key from the "just pressed" set so polling code below skips it. */
  consumeKey(code: string): void {
    this.justPressedKeys.delete(code);
  }

  /**
   * Registers a synchronous callback invoked the moment a new keydown is
   * received. Use this when the action must happen inside the original user
   * gesture (e.g. requestPointerLock).
   */
  onKeyDown(callback: (code: string) => void): () => void {
    this.keyDownCallbacks.add(callback);
    return () => {
      this.keyDownCallbacks.delete(callback);
    };
  }

  isMouseDown(button: number): boolean {
    return this.pressedButtons.has(button);
  }

  getPointer(): Readonly<{ x: number; y: number }> {
    return { x: this.pointerX, y: this.pointerY };
  }

  getPointerDeltaX(): number {
    return this.pointerDeltaX;
  }

  getPointerDeltaY(): number {
    return this.pointerDeltaY;
  }

  endFrame(): void {
    this.justPressedKeys.clear();
    this.pointerDeltaX = 0;
    this.pointerDeltaY = 0;
  }

  dispose(): void {
    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('keyup', this.handleKeyUp);
    window.removeEventListener('mousedown', this.handleMouseDown);
    window.removeEventListener('mouseup', this.handleMouseUp);
    window.removeEventListener('mousemove', this.handleMouseMove);
    window.removeEventListener('blur', this.handleBlur);
    document.removeEventListener('pointerlockchange', this.handlePointerLockChange);
    this.pressedKeys.clear();
    this.justPressedKeys.clear();
    this.pressedButtons.clear();
  }

  private handleKeyDown = (event: KeyboardEvent): void => {
    // Tab is reserved for the pause menu — stop the browser from
    // shifting keyboard focus to other UI elements before the game
    // can react. Only preventDefault when nothing is focused on a
    // form input so accessibility nav still works inside menus.
    if (event.code === 'Tab' && !isFormFieldFocused()) {
      event.preventDefault();
    }
    if (!this.pressedKeys.has(event.code)) {
      this.justPressedKeys.add(event.code);
      // Fire synchronous listeners while we're still inside the user gesture
      // so APIs like requestPointerLock work without an extra click.
      for (const cb of this.keyDownCallbacks) cb(event.code);
    }
    this.pressedKeys.add(event.code);
  };

  private handleKeyUp = (event: KeyboardEvent): void => {
    this.pressedKeys.delete(event.code);
  };

  private handleMouseDown = (event: MouseEvent): void => {
    this.pressedButtons.add(event.button);
  };

  private handleMouseUp = (event: MouseEvent): void => {
    this.pressedButtons.delete(event.button);
  };

  private handleMouseMove = (event: MouseEvent): void => {
    if (this.pointerLocked) {
      this.pointerDeltaX += event.movementX;
      this.pointerDeltaY += event.movementY;
    }
    this.pointerX = event.clientX;
    this.pointerY = event.clientY;
  };

  private handleBlur = (): void => {
    this.pressedKeys.clear();
    this.justPressedKeys.clear();
    this.pressedButtons.clear();
  };

  private handlePointerLockChange = (): void => {
    this.pointerLocked = document.pointerLockElement === this.pointerLockTarget;
  };
}

/** True when an input/textarea/contenteditable element currently owns
 *  keyboard focus. Used to gate the Tab preventDefault — overlay forms
 *  still get normal Tab navigation. */
function isFormFieldFocused(): boolean {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return (el as HTMLElement).isContentEditable === true;
}
